/**
 * FuarAlaniListPage.jsx — Fuar Alanları (backlog #26, 2026-09-15)
 * Route: /Management/FairAreas   (eski /FairAreas buraya yönleniyor)
 *
 * CANLI: GET /fuar/alani/list → [{ fuarAlaniId, fuarAlaniName, salonCount, fuarCount }].
 * Ekleme/düzenleme MODALDA DEĞİL, ayrı sayfada (FuarAlaniFormPage.jsx); salon yönetimi ve
 * salonların varsayılan yükseklikleri Salonlar sayfasında (FuarAlaniSalonlarPage.jsx).
 *
 * ⚠️ Backend'de fuar alanı SİLME yok — bu yüzden liste silme düğmesi göstermiyor.
 * ⚠️ Eski pages/Management/FairAreas/index.js (modal + mock servis) dosyası dokunulmadan duruyor.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Container, Card, CardBody, CardHeader, Row, Col, Button, Input, Table, Badge, Spinner, Alert,
} from "reactstrap";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import { listFuarAlanlari } from "../../../services/fuarAlaniSalonService";
import { useTranslation } from "react-i18next";
import i18n from "../../../i18n";

const normalize = (s) => (s || "").toLocaleLowerCase("tr-TR");

export default function FuarAlaniListPage() {
  const { t } = useTranslation("yonetim");
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => { document.title = i18n.t("yonetim:fuarAlaniList.fuarAlanlariFds"); }, []);

  // KURAL: fetch Effect içindeki async fonksiyonda (react-hooks/set-state-in-effect).
  useEffect(() => {
    let ignore = false;
    async function run() {
      setLoading(true);
      setError(null);
      try {
        const list = await listFuarAlanlari();
        if (!ignore) setRows(list);
      } catch (e) {
        if (!ignore) setError(e.message);
      } finally {
        if (!ignore) setLoading(false);
      }
    }
    run();
    return () => { ignore = true; };
  }, [reloadToken]);

  const filtered = useMemo(() => {
    const q = normalize(search.trim());
    if (!q) return rows;
    return rows.filter((r) => [r.fuarAlaniName, String(r.fuarAlaniId)].some((v) => normalize(v).includes(q)));
  }, [rows, search]);

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title={t("fuarAlaniList.fuarAlanlari")} pageTitle={t("fuarAlaniList.fuarlar")} />

        {error && <Alert color="danger" toggle={() => setError(null)}>{error}</Alert>}

        <Card className="mb-3">
          <CardBody className="py-3">
            <Row className="g-2 align-items-center">
              <Col md={5}>
                <div className="search-box">
                  <Input
                    className="form-control search" placeholder={t("fuarAlaniList.fuarAlaniAdiVeyaId")}
                    value={search} onChange={(e) => setSearch(e.target.value)}
                  />
                  <i className="ri-search-line search-icon" />
                </div>
              </Col>
              <Col md={7} className="d-flex gap-2 justify-content-md-end">
                <Button color="secondary" outline onClick={() => setReloadToken((n) => n + 1)} disabled={loading}>
                  <i className="bx bx-refresh me-1" />{t("common:yenile")}
                </Button>
                <Link to="/Management/FairAreas/new" className="btn btn-primary">
                  <i className="bx bx-plus me-1" />{t("fuarAlaniList.yeniFuarAlani")}
                </Link>
              </Col>
            </Row>
          </CardBody>
        </Card>

        <Card>
          <CardHeader className="d-flex align-items-center">
            <h6 className="card-title mb-0 flex-grow-1">
              {t("fuarAlaniList.fuarAlanlari")}{" "}<Badge color="light" className="text-dark ms-1">{filtered.length}</Badge>
            </h6>
          </CardHeader>
          <CardBody className="p-0">
            {loading ? (
              <div className="text-center py-5"><Spinner color="primary" /></div>
            ) : (
              <div className="table-responsive">
                <Table className="align-middle table-nowrap table-hover mb-0">
                  <thead className="table-light">
                    <tr>
                      <th className="ps-3" style={{ width: 70 }}>#</th>
                      <th>{t("fuarAlaniList.fuarAlani")}</th>
                      {/* Backlog #50: WAS "Salon" / "Fuar" — cells were already counts (salonCount /
                          fuarCount from GET /fuar/alani/list), the header just didn't say so. */}
                      <th className="text-center" style={{ width: 120 }}>{t("fuarAlaniList.salonSayisi")}</th>
                      <th className="text-center" style={{ width: 120 }}>{t("fuarAlaniList.fuarSayisi")}</th>
                      <th className="text-end pe-3" style={{ width: 140 }}>{t("fuarAlaniList.islemler")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.length === 0 && (
                      <tr><td colSpan={5} className="text-center text-muted py-4">{t("common:kayitYok")}</td></tr>
                    )}
                    {filtered.map((r) => (
                      <tr key={r.fuarAlaniId} role="button" onClick={() => navigate(`/Management/FairAreas/${r.fuarAlaniId}`)}>
                        <td className="ps-3 text-muted">{r.fuarAlaniId}</td>
                        <td className="fw-semibold">{r.fuarAlaniName}</td>
                        <td className="text-center"><Badge color="info" pill>{r.salonCount ?? 0}</Badge></td>
                        <td className="text-center"><Badge color="light" className="text-dark" pill>{r.fuarCount ?? 0}</Badge></td>
                        <td className="text-end pe-3" onClick={(e) => e.stopPropagation()}>
                          <div className="d-flex gap-1 justify-content-end">
                            <Link
                              to={`/Management/FairAreas/${r.fuarAlaniId}/Salonlar`}
                              className="btn btn-sm btn-soft-info" title={t("fuarAlaniList.salonlar")}
                            >
                              <i className="bx bx-buildings" />
                            </Link>
                            {/* Backlog #51 (2026-09-20): WAS the edit form directly ("Detay / Düzenle",
                                bx-edit) — this now opens the real detail page; edit lives one click
                                further in, from the detail page's own Düzenle button. */}
                            <Link
                              to={`/Management/FairAreas/${r.fuarAlaniId}`}
                              className="btn btn-sm btn-soft-info" title={t("common:detay")}
                            >
                              <i className="bx bx-show" />
                            </Link>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </CardBody>
        </Card>
      </Container>
    </div>
  );
}
